/**
 * HmFiscalStations — stanowiska z drukarką fiskalną i ich konfiguracja.
 *
 * Ten ekran robi trzy rzeczy, których nie da się zrobić nigdzie indziej:
 *
 *  1. **Zakłada stanowisko i pokazuje token RAZ.** Token trafia do konfiguracji usługi na
 *     stacji roboczej; my trzymamy wyłącznie jego skrót, więc zgubiony token trzeba wygenerować
 *     od nowa. Mówimy to wprost, zamiast dawać do skopiowania coś, co „pewnie da się podejrzeć".
 *
 *  2. **Przypisuje stawki VAT do liter urządzenia.** To jest najważniejsza i najbardziej
 *     niebezpieczna rzecz na tym ekranie: litery nadał serwisant w pamięci fiskalnej konkretnej
 *     drukarki, a pomyłka nie objawi się błędem — objawi się sprzedażą zaksięgowaną na złej
 *     stawce. Dlatego niczego tu nie podpowiadamy „na oko" i nie ma pola „domyślna litera".
 *
 *  3. **Pokazuje, czy usługa żyje** i co widzi na drukarce (papier, tryb szkoleniowy). Bez tego
 *     pierwszą informacją o problemie byłby brak paragonu przy kliencie.
 *
 * Czego tu świadomie NIE MA: ręcznego wpisywania numeru unikatowego kasy. Odczytuje go usługa
 * z urządzenia; wpisany z pamięci przypisałby paragony do nieistniejącego sprzętu.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { FiscalAgentDownloads } from '@/components/handel-magazyn/FiscalAgentDownloads';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import {
  type FiscalStationListItem,
  getSymfoniaDictionaries,
  listStations,
} from '@/lib/fiscal/fiscal-api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

const VAT_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const;
const PAYMENT_TYPES = ['CASH', 'CARD', 'TRANSFER', 'VOUCHER', 'CREDIT', 'OTHER'] as const;

export function HmFiscalStations() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const companyId = useActiveCompanyScope().companyId;

  const [createOpen, setCreateOpen] = useState(false);
  const [freshToken, setFreshToken] = useState<string | null>(null);
  const [editing, setEditing] = useState<FiscalStationListItem | null>(null);

  const stationsQuery = useQuery({
    queryKey: ['fiscal', 'stations', companyId],
    queryFn: () => listStations(companyId!),
    enabled: !!companyId,
  });

  if (!companyId) {
    return (
      <div className="p-4 text-sm text-text-secondary">
        {t('tenant.handelMagazyn.stations.pickCompany', 'Wybierz firmę.')}
      </div>
    );
  }

  const stations = stationsQuery.data ?? [];

  return (
    <div className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-text-primary">
          {t('tenant.handelMagazyn.stations.title', 'Drukarki fiskalne')}
        </h1>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setCreateOpen(true)}
          data-testid="hm-station-new"
        >
          {t('tenant.handelMagazyn.stations.add', 'Dodaj stanowisko')}
        </Button>
      </div>

      {stationsQuery.isLoading ? (
        <p className="text-sm text-text-secondary">{t('common.loading')}</p>
      ) : stations.length === 0 ? (
        <p className="text-sm text-text-secondary" data-testid="hm-stations-empty">
          {t(
            'tenant.handelMagazyn.stations.empty',
            'Nie ma jeszcze żadnego stanowiska. Dodaj je i wgraj token do usługi na stacji z drukarką.',
          )}
        </p>
      ) : (
        <div className="space-y-2">
          {stations.map((station) => (
            <StationRow key={station.id} station={station} onEdit={() => setEditing(station)} />
          ))}
        </div>
      )}

      {/* Instalatory i instrukcja pod listą — to moment, w którym człowiek ma token i musi
          postawić usługę na komputerze przy drukarce. */}
      <FiscalAgentDownloads companyId={companyId} />

      {createOpen && (
        <CreateStationModal
          companyId={companyId}
          onClose={() => setCreateOpen(false)}
          onCreated={(token) => {
            setCreateOpen(false);
            setFreshToken(token);
            void queryClient.invalidateQueries({ queryKey: ['fiscal', 'stations'] });
          }}
        />
      )}

      {freshToken && <TokenModal token={freshToken} onClose={() => setFreshToken(null)} />}

      {editing && (
        <StationMapsModal
          station={editing}
          companyId={companyId}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.show({
              variant: 'success',
              title: t('tenant.handelMagazyn.stations.saved', 'Zapisano konfigurację stanowiska'),
            });
            void queryClient.invalidateQueries({ queryKey: ['fiscal', 'stations'] });
          }}
        />
      )}
    </div>
  );
}

/** Stawki odczytane z urządzenia (litera → stawka), przysłane przez usługę w pulsie. */
function readDeviceRates(station: FiscalStationListItem): Record<string, string> | null {
  const status = (station.printerStatus ?? {}) as { vatRates?: unknown };
  const rates = status.vatRates;
  if (!rates || typeof rates !== 'object') return null;
  const entries = Object.entries(rates as Record<string, unknown>).filter(
    ([, value]) => typeof value === 'string',
  ) as Array<[string, string]>;
  return entries.length > 0 ? Object.fromEntries(entries) : null;
}

/** Adres i strona kodowa w jednej linii — to, po czym poznaje się stanowisko przy awarii. */
function connectionLabel(station: FiscalStationListItem): string {
  const config = (station.connectionConfig ?? {}) as Record<string, unknown>;
  const where =
    station.connectionKind === 'ethernet'
      ? [config.host, config.port].filter(Boolean).join(':')
      : String(config.port ?? '');
  const page = typeof config.codePage === 'string' ? config.codePage : null;
  return [where, page].filter(Boolean).join(' · ');
}

function StationRow({ station, onEdit }: { station: FiscalStationListItem; onEdit: () => void }) {
  const { t } = useTranslation();
  const status = (station.printerStatus ?? {}) as { paperOut?: boolean; trainingMode?: boolean };

  return (
    <div
      className="flex items-center justify-between rounded border border-border-primary bg-bg-secondary px-3 py-2"
      data-testid={`hm-station-${station.code}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-text-primary">{station.name}</span>
          <span className="text-xs text-text-secondary">
            {station.printerVendor}
            {station.printerModel ? ` ${station.printerModel}` : ''}
          </span>
          <span
            className={
              station.online
                ? 'rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800 dark:bg-green-900/30 dark:text-green-300'
                : 'rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300'
            }
            data-testid={`hm-station-online-${station.code}`}
          >
            {station.online
              ? t('tenant.handelMagazyn.stations.online', 'usługa działa')
              : t('tenant.handelMagazyn.stations.offline', 'usługa nie odpowiada')}
          </span>
        </div>
        <div className="mt-0.5 text-xs text-text-secondary">
          {station.fiscalDeviceNo
            ? t('tenant.handelMagazyn.stations.device', 'Drukarka: {{no}}', {
                no: station.fiscalDeviceNo,
              })
            : t(
                'tenant.handelMagazyn.stations.deviceUnknown',
                'Numer drukarki nieznany — usługa jeszcze się nie zgłosiła.',
              )}
          {/* Tryb szkoleniowy to NIE usterka — to jedyny bezpieczny tryb do testów. Ale musi
              być widoczny, żeby nikt nie wziął wydruku niefiskalnego za paragon. */}
          {status.trainingMode
            ? ` · ${t('tenant.handelMagazyn.stations.training', 'tryb szkoleniowy')}`
            : ''}
          {status.paperOut
            ? ` · ${t('tenant.handelMagazyn.stations.paperOut', 'brak papieru')}`
            : ''}
          {station.vatLetterMap.length === 0
            ? ` · ${t('tenant.handelMagazyn.stations.noVatMap', 'brak mapy stawek — druk się nie uda')}`
            : ''}
          {connectionLabel(station) ? ` · ${connectionLabel(station)}` : ''}
        </div>
      </div>
      <Button
        variant="secondary"
        size="sm"
        onClick={onEdit}
        data-testid={`hm-station-edit-${station.code}`}
      >
        {t('tenant.handelMagazyn.stations.configure', 'Konfiguruj')}
      </Button>
    </div>
  );
}

function CreateStationModal({
  companyId,
  onClose,
  onCreated,
}: {
  companyId: string;
  onClose: () => void;
  onCreated: (token: string) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [vendor, setVendor] = useState<'posnet' | 'elzab' | 'novitus'>('elzab');
  const [connectionKind, setConnectionKind] = useState<'serial' | 'ethernet'>('ethernet');
  const [model, setModel] = useState('');
  // Adres i port drukarki w sieci klienta. Port NIE jest standardowy — w ELZAB Mera online
  // ustawiony jest 1001, a typowe porty drukarkowe (9100) są tam zamknięte.
  const [host, setHost] = useState('');
  const [tcpPort, setTcpPort] = useState('1001');
  const [serialPort, setSerialPort] = useState('COM1');
  const [baudRate, setBaudRate] = useState('9600');
  // Strona kodowa jest USTAWIENIEM URZĄDZENIA — zła daje dziury zamiast polskich liter.
  const [codePage, setCodePage] = useState<'cp852' | 'cp1250' | 'mazovia' | 'ascii'>('cp852');
  const [maxItemNameLength, setMaxItemNameLength] = useState('42');

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ id: string; token: string }>(
        `/fiscal/stations?companyId=${companyId}`,
        {
          name,
          code,
          printerVendor: vendor,
          printerModel: model.trim() || undefined,
          connectionKind,
          maxItemNameLength: Number(maxItemNameLength),
          connectionConfig:
            connectionKind === 'ethernet'
              ? { host: host.trim(), port: Number(tcpPort), codePage }
              : { port: serialPort.trim(), baudRate: Number(baudRate), codePage },
        },
      );
      return res.data;
    },
    onSuccess: (data) => onCreated(data.token),
    onError: () =>
      toast.show({
        variant: 'error',
        title: t('tenant.handelMagazyn.stations.createFailed', 'Nie udało się dodać stanowiska'),
      }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={t('tenant.handelMagazyn.stations.add', 'Dodaj stanowisko')}
    >
      <div className="space-y-3">
        <Input
          label={t('tenant.handelMagazyn.stations.name', 'Nazwa')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="hm-station-name"
        />
        <Input
          label={t('tenant.handelMagazyn.stations.code', 'Kod')}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          hint={t(
            'tenant.handelMagazyn.stations.codeHint',
            'Krótki identyfikator do konfiguracji usługi i dziennika zdarzeń.',
          )}
          data-testid="hm-station-code"
        />
        <Select
          label={t('tenant.handelMagazyn.stations.vendor', 'Marka drukarki')}
          value={vendor}
          onChange={(e) => setVendor(e.target.value as typeof vendor)}
          options={[
            { value: 'elzab', label: 'Elzab' },
            { value: 'posnet', label: 'Posnet' },
            { value: 'novitus', label: 'Novitus' },
          ]}
        />
        <Input
          label={t('tenant.handelMagazyn.stations.model', 'Model')}
          value={model}
          onChange={(e) => setModel(e.target.value)}
          hint={t(
            'tenant.handelMagazyn.stations.modelHint',
            'Limit długości nazwy i obsługiwane rozkazy zależą od MODELU, nie od marki.',
          )}
          data-testid="hm-station-model"
        />
        <Select
          label={t('tenant.handelMagazyn.stations.connection', 'Połączenie')}
          value={connectionKind}
          onChange={(e) => setConnectionKind(e.target.value as typeof connectionKind)}
          options={[
            {
              value: 'ethernet',
              label: t('tenant.handelMagazyn.stations.ethernet', 'Sieć (Ethernet)'),
            },
            { value: 'serial', label: t('tenant.handelMagazyn.stations.serial', 'Port szeregowy') },
          ]}
        />
        {connectionKind === 'ethernet' ? (
          <div className="flex gap-2">
            <Input
              label={t('tenant.handelMagazyn.stations.host', 'Adres drukarki')}
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="192.168.1.188"
              data-testid="hm-station-host"
            />
            <Input
              label={t('tenant.handelMagazyn.stations.tcpPort', 'Port TCP')}
              value={tcpPort}
              onChange={(e) => setTcpPort(e.target.value)}
              hint={t(
                'tenant.handelMagazyn.stations.tcpPortHint',
                'Ustawienie drukarki — nie zgaduj, sprawdź w jej menu.',
              )}
              data-testid="hm-station-tcp-port"
            />
          </div>
        ) : (
          <div className="flex gap-2">
            <Input
              label={t('tenant.handelMagazyn.stations.serialPort', 'Port szeregowy')}
              value={serialPort}
              onChange={(e) => setSerialPort(e.target.value)}
              data-testid="hm-station-serial-port"
            />
            <Input
              label={t('tenant.handelMagazyn.stations.baudRate', 'Prędkość')}
              value={baudRate}
              onChange={(e) => setBaudRate(e.target.value)}
              data-testid="hm-station-baud"
            />
          </div>
        )}
        <Select
          label={t('tenant.handelMagazyn.stations.codePage', 'Strona kodowa')}
          value={codePage}
          onChange={(e) => setCodePage(e.target.value as typeof codePage)}
          hint={t(
            'tenant.handelMagazyn.stations.codePageHint',
            'Ustawienie urządzenia. Zła strona kodowa drukuje dziury zamiast polskich liter — sprawdź na wydruku testowym.',
          )}
          options={[
            { value: 'cp852', label: 'CP852 (Latin 2)' },
            { value: 'cp1250', label: 'CP1250 (Windows)' },
            { value: 'mazovia', label: 'Mazovia' },
            {
              value: 'ascii',
              label: t('tenant.handelMagazyn.stations.asciiPage', 'bez polskich znaków'),
            },
          ]}
          data-testid="hm-station-codepage"
        />
        <Input
          label={t('tenant.handelMagazyn.stations.maxName', 'Limit długości nazwy towaru')}
          value={maxItemNameLength}
          onChange={(e) => setMaxItemNameLength(e.target.value)}
          hint={t(
            'tenant.handelMagazyn.stations.maxNameHint',
            'Z karty katalogowej modelu. Dla ELZAB Mera online to 42 znaki.',
          )}
          data-testid="hm-station-maxname"
        />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={!name.trim() || !code.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
            data-testid="hm-station-create"
          >
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Token pokazywany RAZ — w bazie mamy tylko skrót, więc drugiego razu nie będzie. */
function TokenModal({ token, onClose }: { token: string; onClose: () => void }) {
  const { t } = useTranslation();
  const tokenRef = useRef<HTMLElement>(null);
  // Fokus startowy na tokenie, nie na X: Enter/Spacja tuż po otwarciu nie zamyka okna (token widać
  // tylko raz). React nie obsługuje `autoFocus` na <code>, więc ustawiamy go ręcznie; efekt dziecka
  // biegnie przed efektem rdzenia okna, który uszanuje fokus już będący w panelu.
  useEffect(() => {
    tokenRef.current?.focus();
  }, []);
  return (
    <Modal
      open
      onClose={onClose}
      title={t('tenant.handelMagazyn.stations.token', 'Token stanowiska')}
      // Jednorazowy sekret: tło i Esc go nie chowają, zamyka tylko X lub „Zamknij".
      closeOnBackdrop={false}
      closeOnEsc={false}
    >
      <div className="space-y-3">
        <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          {t(
            'tenant.handelMagazyn.stations.tokenWarning',
            'Skopiuj teraz — token pokazujemy tylko raz. Później trzeba będzie wygenerować nowy.',
          )}
        </p>
        <code
          className="block break-all rounded bg-bg-tertiary px-3 py-2 text-sm"
          data-testid="hm-station-token"
          ref={tokenRef}
          // Fokusowalny, żeby dało się go zaznaczyć z klawiatury.
          tabIndex={0}
        >
          {token}
        </code>
        <div className="flex justify-end">
          <Button variant="primary" onClick={onClose}>
            {t('common.close', 'Zamknij')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Mapy stawek i płatności. Identyfikatory wpisuje człowiek, bo to on ma przed sobą wydruk
 * z ustawieniami urządzenia i słownik Symfonii — a zgadywanie kończyłoby się sprzedażą na
 * złej stawce.
 */
function StationMapsModal({
  station,
  companyId,
  onClose,
  onSaved,
}: {
  station: FiscalStationListItem;
  companyId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [vatRows, setVatRows] = useState(
    station.vatLetterMap.map((row) => ({
      vatRateId: String(row.vatRateId),
      vatRateCode: row.vatRateCode ?? '',
      letter: row.letter,
    })),
  );
  const deviceRates = readDeviceRates(station);

  /**
   * Słowniki Z SYMFONII. Bez nich ten ekran kazałby wpisywać z pamięci identyfikatory stawek
   * i form płatności — a pomyłka nie objawia się błędem, tylko sprzedażą na złej stawce.
   */
  const dictionariesQuery = useQuery({
    queryKey: ['fiscal', 'symfonia-dictionaries', companyId],
    queryFn: () => getSymfoniaDictionaries(companyId),
    retry: false,
  });
  const dictionaries = dictionariesQuery.data;
  const [paymentRows, setPaymentRows] = useState(
    station.paymentTypeMap.map((row) => ({
      paymentFormId: String(row.paymentFormId),
      paymentFormName: row.paymentFormName ?? '',
      type: row.type,
    })),
  );

  const mutation = useMutation({
    mutationFn: async () => {
      await api.patch(`/fiscal/stations/${station.id}?companyId=${companyId}`, {
        vatLetterMap: vatRows
          .filter((r) => r.vatRateId.trim())
          .map((r) => ({
            vatRateId: Number(r.vatRateId),
            vatRateCode: r.vatRateCode || null,
            letter: r.letter,
          })),
        paymentTypeMap: paymentRows
          .filter((r) => r.paymentFormId.trim())
          .map((r) => ({
            paymentFormId: Number(r.paymentFormId),
            paymentFormName: r.paymentFormName || null,
            type: r.type,
          })),
      });
    },
    onSuccess: onSaved,
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`${station.name} — ${t('tenant.handelMagazyn.stations.maps', 'mapy')}`}
    >
      <div className="space-y-4">
        <section>
          <h3 className="mb-1 text-sm font-semibold text-text-primary">
            {t('tenant.handelMagazyn.stations.vatMap', 'Stawki VAT → litery drukarki')}
          </h3>
          <p className="mb-2 text-xs text-text-secondary">
            {t(
              'tenant.handelMagazyn.stations.vatMapHint',
              'Litery nadał serwisant w pamięci fiskalnej TEJ drukarki. Sprawdź je na wydruku ustawień — błędne przypisanie zaksięguje sprzedaż na złej stawce.',
            )}
          </p>
          {/* Stawki ODCZYTANE z urządzenia. Pokazujemy je jako podpowiedź, ale nie wpisujemy
              do mapy automatycznie: odczyt mówi, co siedzi w pamięci fiskalnej, a nie którą
              stawkę Symfonia uznaje za „tę 23%". To potwierdza człowiek. */}
          {dictionariesQuery.isError && (
            <p
              className="mb-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
              data-testid="hm-station-dict-error"
              role="alert"
            >
              {t(
                'tenant.handelMagazyn.stations.dictError',
                'Nie udało się pobrać stawek z Symfonii — sprawdź połączenie w ustawieniach modułu. Bez nich nie da się bezpiecznie ustawić map.',
              )}
            </p>
          )}
          {deviceRates && (
            <p
              className="mb-2 rounded bg-bg-tertiary px-2 py-1 text-xs text-text-secondary"
              data-testid="hm-station-device-rates"
            >
              {t('tenant.handelMagazyn.stations.deviceRates', 'Odczytane z drukarki:')}{' '}
              {Object.entries(deviceRates)
                .map(([letter, rate]) => `${letter} = ${rate}`)
                .join(', ')}
            </p>
          )}
          {vatRows.map((row, index) => (
            <div key={index} className="mb-2 flex items-center gap-2">
              <Select
                containerClassName="flex-1"
                value={row.vatRateId}
                onChange={(e) => {
                  const picked = dictionaries?.vatRates.find(
                    (rate) => String(rate.id) === e.target.value,
                  );
                  setVatRows((rows) =>
                    rows.map((r, i) =>
                      i === index
                        ? {
                            ...r,
                            vatRateId: e.target.value,
                            // Opis zapamiętujemy razem z identyfikatorem — po zmianie słownika
                            // w Symfonii widać wtedy, że wpis wskazuje na coś, czego już nie ma.
                            vatRateCode: picked ? (picked.code ?? '') : r.vatRateCode,
                          }
                        : r,
                    ),
                  );
                }}
                placeholder={t('tenant.handelMagazyn.stations.pickRate', 'Wybierz stawkę…')}
                options={(dictionaries?.vatRates ?? []).map((rate) => ({
                  value: String(rate.id),
                  label: rate.description ? `${rate.code} — ${rate.description}` : rate.code,
                }))}
                data-testid={`hm-station-vat-id-${index}`}
              />
              <span className="text-text-secondary">→</span>
              <Select
                value={row.letter}
                onChange={(e) =>
                  setVatRows((rows) =>
                    rows.map((r, i) => (i === index ? { ...r, letter: e.target.value } : r)),
                  )
                }
                options={VAT_LETTERS.map((letter) => ({
                  value: letter,
                  // Przy literze pokazujemy stawkę ODCZYTANĄ z drukarki — to ona rozstrzyga,
                  // co naprawdę siedzi w pamięci fiskalnej tego egzemplarza.
                  label: deviceRates?.[letter] ? `${letter} (${deviceRates[letter]})` : letter,
                }))}
                data-testid={`hm-station-vat-letter-${index}`}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setVatRows((rows) => rows.filter((_, i) => i !== index))}
                data-testid={`hm-station-vat-remove-${index}`}
              >
                {t('common.delete', 'Usuń')}
              </Button>
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setVatRows((rows) => [...rows, { vatRateId: '', vatRateCode: '', letter: 'A' }])
            }
            data-testid="hm-station-vat-add"
          >
            {t('tenant.handelMagazyn.stations.addRow', 'Dodaj wiersz')}
          </Button>
        </section>

        <section>
          <h3 className="mb-1 text-sm font-semibold text-text-primary">
            {t('tenant.handelMagazyn.stations.paymentMap', 'Formy płatności → typy')}
          </h3>
          {paymentRows.map((row, index) => (
            <div key={index} className="mb-2 flex items-center gap-2">
              <Select
                containerClassName="flex-1"
                value={row.paymentFormId}
                onChange={(e) => {
                  const picked = dictionaries?.paymentForms.find(
                    (form) => String(form.id) === e.target.value,
                  );
                  setPaymentRows((rows) =>
                    rows.map((r, i) =>
                      i === index
                        ? {
                            ...r,
                            paymentFormId: e.target.value,
                            paymentFormName: picked ? picked.name : r.paymentFormName,
                          }
                        : r,
                    ),
                  );
                }}
                placeholder={t('tenant.handelMagazyn.stations.pickForm', 'Wybierz formę…')}
                options={(dictionaries?.paymentForms ?? []).map((form) => ({
                  value: String(form.id),
                  label: form.name,
                }))}
                data-testid={`hm-station-pay-id-${index}`}
              />
              <span className="text-text-secondary">→</span>
              <Select
                value={row.type}
                onChange={(e) =>
                  setPaymentRows((rows) =>
                    rows.map((r, i) => (i === index ? { ...r, type: e.target.value } : r)),
                  )
                }
                options={PAYMENT_TYPES.map((type) => ({
                  value: type,
                  label: t(`tenant.handelMagazyn.stations.paymentType.${type}`, type),
                }))}
                data-testid={`hm-station-pay-type-${index}`}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPaymentRows((rows) => rows.filter((_, i) => i !== index))}
                data-testid={`hm-station-pay-remove-${index}`}
              >
                {t('common.delete', 'Usuń')}
              </Button>
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              setPaymentRows((rows) => [
                ...rows,
                { paymentFormId: '', paymentFormName: '', type: 'CASH' },
              ])
            }
            data-testid="hm-station-pay-add"
          >
            {t('tenant.handelMagazyn.stations.addRow', 'Dodaj wiersz')}
          </Button>
        </section>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
            data-testid="hm-station-save-maps"
          >
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
